import { useEffect, useState } from 'react';
import { useHistory } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import useWebSocket from 'react-use-websocket';
import { WebSocketHook } from 'react-use-websocket/dist/lib/types';
import { useVerificationId } from 'src/common/hooks/useVerificationId';
import { VerificationStatus } from 'src/common/models/verificationModels';
import { FlowType, ProcessStatusDTO } from 'src/common/models/processModels';
import {
  getRedirectUrlForStatus,
  handleRedirect,
  isPartnerFlowWithoutRetry,
} from 'src/common/services/process';
import { environment } from 'src/config/environment';
import { useGetWssUrlQuery } from 'src/pages/AspspSelectPage/webSdk/useGetWssUrlQuery';
import { useRedirectUrlQuery } from 'src/pages/VerificationPage/verificationQueries';
import { verificationQueryKeys } from 'src/common/verificationQueryKeys';
import {
  AspspSelectPageRoute,
  verificationIdParam,
  VerificationPageRoute,
} from 'src/routes';
import { Jsonify } from 'type-fest';
import { notifyRedirectError } from '../../../common/components/Toast';

interface TypedWebsocketHook extends Omit<WebSocketHook, 'lastJsonMessage'> {
  lastJsonMessage: Jsonify<{
    verificationId: string;
    verificationStatus: VerificationStatus;
  }> | null;
}

export const useSdkAspspHandler = () => {
  const [openedWindow, setOpenedWindow] = useState<Window | null>(null);
  const [isLoginProcessing, setIsLoginProcessing] = useState(false);

  const history = useHistory();
  const queryClient = useQueryClient();
  const verificationId = useVerificationId();
  const { data: redirectData } = useRedirectUrlQuery(verificationId);
  const enabled = !!environment.isWebSdk;

  const wssUrl = useGetWssUrlQuery({ verificationId, enabled });
  const { lastJsonMessage }: TypedWebsocketHook = useWebSocket(
    wssUrl.data || null,
    {
      share: true,
      shouldReconnect: () => true,
    }
  );

  //todo: should include redirectData and only take action when both are present?
  useEffect(() => {
    if (!lastJsonMessage) return;

    const status = lastJsonMessage.verificationStatus;

    // BusinessConsentGranted: close bank tab and switch SDK window to AspspSelectPage.
    // Set status in cache so RequireCorrectStep sees BusinessConsentGranted and doesn't redirect to /redirect.
    if (status === VerificationStatus.BusinessConsentGranted) {
      openedWindow?.close();
      setIsLoginProcessing(false);
      const id = lastJsonMessage.verificationId || verificationId;
      if (id) {
        const statusData: ProcessStatusDTO = {
          verificationId: id,
          verificationStatus: VerificationStatus.BusinessConsentGranted,
          verificationScoring: 0,
          verificationFlow: FlowType.Regular,
        };
        queryClient.setQueryData(verificationQueryKeys.status(id), statusData);
      }
      history.replace({
        pathname: AspspSelectPageRoute,
        search: verificationId
          ? `?${verificationIdParam}=${verificationId}`
          : '',
      });
      notifyRedirectError('nieudane logowanie');
    }

    // Other terminal statuses: close tab and redirect or go to verification page
    if (
      ![
        VerificationStatus.AspspsSelected,
        VerificationStatus.BusinessConsentGranted,
      ].includes(status)
    ) {
      openedWindow?.close();

      if (
        redirectData &&
        isPartnerFlowWithoutRetry(
          redirectData.processFlow,
          redirectData.processRetry
        )
      ) {
        handleRedirect(getRedirectUrlForStatus(status, redirectData));
      } else {
        history.replace(VerificationPageRoute);
      }
    }
  }, [
    lastJsonMessage,
    redirectData,
    openedWindow,
    history,
    verificationId,
    queryClient,
  ]);

  return {
    openedWindow,
    setOpenedWindow,
    isLoginProcessing,
    setIsLoginProcessing,
  };
};
